import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase, isSupabaseReady } from '../supabase';
import { useAuth } from './AuthContext';

export const UsersContext = createContext();

// ── Role definitions (unchanged) ──────────────────────────────────────────────
export const ROLES = {
  superadmin: { label: 'Super Administrador', level: 3 },
  admin:      { label: 'Administrador',        level: 2 },
  sales:      { label: 'Ventas',               level: 1 },
};

export const canManageUser = (actorRole, targetRole) => {
  if (actorRole === 'superadmin') return true;
  if (actorRole === 'admin') return targetRole !== 'superadmin';
  return false;
};

export const visibleUsersFor = (users, actorRole) => {
  if (actorRole === 'superadmin') return users;
  return users.filter(u => u.role !== 'superadmin');
};

export const hasOtherActiveSuperAdmin = (users, excludeId) =>
  users.some(u => u.role === 'superadmin' && u.status === 'active' && u.id !== excludeId);

// ── localStorage fallback data (used when Supabase is not configured) ─────────
const FALLBACK_USERS = [
  { id: 'u0', name: 'Dev Root',    email: 'dev@system.internal', password: 'superadmin123', role: 'superadmin', status: 'active', createdAt: new Date(Date.now() - 86400000 * 60).toISOString() },
  { id: 'u1', name: 'Sarah Owner', email: 'sarah@company.com',   password: 'admin123',      role: 'admin',      status: 'active', createdAt: new Date(Date.now() - 86400000 * 30).toISOString() },
  { id: 'u2', name: 'Mark Sales',  email: 'mark@company.com',    password: 'sales123',      role: 'sales',      status: 'active', createdAt: new Date(Date.now() - 86400000 * 10).toISOString() },
  { id: 'u3', name: 'Lisa Sales',  email: 'lisa@company.com',    password: 'sales123',      role: 'sales',      status: 'paused', createdAt: new Date(Date.now() - 86400000 * 5).toISOString() },
];

// ── Provider ──────────────────────────────────────────────────────────────────
export const UsersProvider = ({ children }) => {
  const { profile, login: authLogin, logout: authLogout, loading: authLoading, isSupabaseReady } = useAuth();

  // Supabase mode: users list for admin pages
  const [supabaseUsers, setSupabaseUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Fallback localStorage mode
  const [localUsers, setLocalUsers] = useState(
    () => JSON.parse(localStorage.getItem('crm_users')) || FALLBACK_USERS
  );
  const [currentUserId, setCurrentUserId] = useState(null);

  // ── Supabase: fetch all visible profiles ────────────────────────────────────
  const fetchSupabaseUsers = async () => {
    if (!isSupabaseReady || !profile) return;
    setUsersLoading(true);
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: true });
    if (!error && data) setSupabaseUsers(data);
    setUsersLoading(false);
  };

  useEffect(() => {
    if (isSupabaseReady && profile) fetchSupabaseUsers();
  }, [isSupabaseReady, profile]);

  // ── Derived current user ────────────────────────────────────────────────────
  const currentUser = isSupabaseReady
    ? profile   // comes from AuthContext → profiles table
    : localUsers.find(u => u.id === currentUserId) || null;

  // ── LOGIN ───────────────────────────────────────────────────────────────────
  const loginUser = async (email, password) => {
    if (!isSupabaseReady) {
      // localStorage fallback
      const user = localUsers.find(
        u => u.email === email && u.password === password && u.status === 'active'
      );
      if (user) setCurrentUserId(user.id);
      return user || null;
    }

    const result = await authLogin(email, password);
    if (result.error) return null;
    return result.profile;
  };

  // ── LOGOUT ──────────────────────────────────────────────────────────────────
  const logoutUser = async () => {
    if (!isSupabaseReady) {
      setCurrentUserId(null);
      return;
    }
    await authLogout();
    setSupabaseUsers([]);
  };

  // ── GET VISIBLE USERS ───────────────────────────────────────────────────────
  const getVisibleUsers = () => {
    if (isSupabaseReady) return visibleUsersFor(supabaseUsers, currentUser?.role);
    return visibleUsersFor(localUsers, currentUser?.role);
  };

  // ── GET USER BY ID ──────────────────────────────────────────────────────────
  const getUserById = (id) => {
    if (isSupabaseReady) return supabaseUsers.find(u => u.id === id) || null;
    return localUsers.find(u => u.id === id) || null;
  };

  // ── ADD USER ────────────────────────────────────────────────────────────────
  // Supabase: creates auth user + profile via Edge Function (service role needed).
  // For now we use the Supabase Admin API pattern — in production replace with Edge Function.
  const addUser = async (data) => {
    if (!isSupabaseReady) {
      const safeRole = data.role === 'superadmin' ? 'admin' : data.role;
      const updated = [
        ...localUsers,
        { ...data, role: safeRole, id: Date.now().toString(), createdAt: new Date().toISOString(), status: 'active' },
      ];
      setLocalUsers(updated);
      localStorage.setItem('crm_users', JSON.stringify(updated));
      return { error: null };
    }

    // NOTE: Creating a Supabase auth user from the client requires the service
    // role key (which must never be in the browser). This is a placeholder that
    // shows the intended flow. In production, call a Supabase Edge Function here.
    // The profile row will be auto-created by the on_auth_user_created trigger.
    console.warn(
      '[UsersContext] addUser via Supabase requires a server-side Edge Function. ' +
      'See SETUP_GUIDE.md for instructions.'
    );
    return { error: 'User creation via Supabase requires an Edge Function. See setup guide.' };
  };

  // ── UPDATE USER ─────────────────────────────────────────────────────────────
  const updateUser = async (id, updates) => {
    if (!isSupabaseReady) {
      const safeUpdates = { ...updates };
      if (currentUser?.role !== 'superadmin' && safeUpdates.role === 'superadmin') {
        delete safeUpdates.role;
      }
      const updated = localUsers.map(u => u.id === id ? { ...u, ...safeUpdates } : u);
      setLocalUsers(updated);
      localStorage.setItem('crm_users', JSON.stringify(updated));
      return { error: null };
    }

    const safeUpdates = { ...updates };
    delete safeUpdates.password; // never update password via profiles table
    if (currentUser?.role !== 'superadmin') delete safeUpdates.role;

    const { error } = await supabase
      .from('profiles')
      .update(safeUpdates)
      .eq('id', id);

    if (!error) await fetchSupabaseUsers();
    return { error: error?.message || null };
  };

  // ── CHANGE PASSWORD ─────────────────────────────────────────────────────────
  // Requires service role — placeholder until Edge Function is set up.
  const changePassword = async (id, _newPassword) => {
    if (!isSupabaseReady) {
      const updated = localUsers.map(u =>
        u.id === id ? { ...u, passwordChangedAt: new Date().toISOString() } : u
      );
      setLocalUsers(updated);
      localStorage.setItem('crm_users', JSON.stringify(updated));
      return { error: null };
    }

    console.warn(
      '[UsersContext] changePassword requires a server-side Edge Function. ' +
      'See SETUP_GUIDE.md for instructions.'
    );
    return { error: 'Password changes require an Edge Function. See setup guide.' };
  };

  // ── SET STATUS ──────────────────────────────────────────────────────────────
  const setStatus = async (id, status) => {
    const allUsers = isSupabaseReady ? supabaseUsers : localUsers;
    const target = allUsers.find(u => u.id === id);
    if (!target) return 'Usuario no encontrado.';

    const deactivating = ['paused', 'blocked', 'archived'];
    if (
      target.role === 'superadmin' &&
      deactivating.includes(status) &&
      !hasOtherActiveSuperAdmin(allUsers, id)
    ) {
      return 'Debe permanecer al menos un Super Administrador activo. Agrega otro primero.';
    }

    if (!isSupabaseReady) {
      const updated = localUsers.map(u => u.id === id ? { ...u, status } : u);
      setLocalUsers(updated);
      localStorage.setItem('crm_users', JSON.stringify(updated));
      return null;
    }

    const { error } = await supabase
      .from('profiles')
      .update({ status })
      .eq('id', id);

    if (error) return error.message;
    await fetchSupabaseUsers();
    return null;
  };

  // ── Loading state ───────────────────────────────────────────────────────────
  const isLoading = authLoading || usersLoading;

  return (
    <UsersContext.Provider value={{
      users: isSupabaseReady ? supabaseUsers : localUsers,
      currentUser,
      currentUserId,
      setCurrentUserId,
      loginUser,
      logoutUser,
      getVisibleUsers,
      getUserById,
      addUser,
      updateUser,
      changePassword,
      setStatus,
      isLoading,
      isSupabaseReady,
      refreshUsers: fetchSupabaseUsers,
    }}>
      {children}
    </UsersContext.Provider>
  );
};

export const useUsers = () => useContext(UsersContext);